import type {
  ModelSource,
  ModelSourceEntry,
  ModelSourceKind,
  ModelSourceTier
} from "../types/model";

/**
 * Anchor preference, most model-fronting first. Mirrors
 * `SOURCE_ANCHOR_PRIORITY` in `scripts/lib/source-entries.mjs`; the data test
 * in `src/data/source-entries.test.ts` asserts both agree on every record.
 */
export const SOURCE_ANCHOR_PRIORITY: ModelSourceKind[] = [
  "official-page",
  "model-card",
  "official-docs",
  "huggingface",
  "github",
  "model-repository",
  "paper",
  "press",
  "aggregator",
  "social",
  "reference",
  "other"
];

export const SOURCE_TIER_ORDER: ModelSourceTier[] = [
  "official",
  "first-party-partner",
  "third-party",
  "secondary"
];

function kindRank(kind: ModelSourceKind | undefined): number {
  const index = SOURCE_ANCHOR_PRIORITY.indexOf(kind as ModelSourceKind);
  return index === -1 ? SOURCE_ANCHOR_PRIORITY.length : index;
}

function tierRank(tier: ModelSourceTier | undefined): number {
  const index = SOURCE_TIER_ORDER.indexOf(tier as ModelSourceTier);
  return index === -1 ? SOURCE_TIER_ORDER.length : index;
}

/**
 * Evidence tier outranks page kind, so a launch partner's model page never
 * fronts the model ahead of the developer's own documentation.
 */
function anchorRank(entry: ModelSourceEntry): number {
  return (
    tierRank(entry.tier) * SOURCE_ANCHOR_PRIORITY.length + kindRank(entry.kind)
  );
}

/**
 * The single link the detail header offers as the model's front door. The
 * ledger is the only input: a record with no entries renders no anchor rather
 * than falling back to a heuristic that could mislabel third-party evidence as
 * official.
 */
export function resolveSourceAnchor(
  source: ModelSource | undefined
): ModelSourceEntry | null {
  let best: ModelSourceEntry | null = null;
  for (const entry of source?.entries ?? []) {
    if (!entry?.url || !entry.kind) continue;
    if (!best) {
      best = entry;
      continue;
    }
    const delta = anchorRank(entry) - anchorRank(best);
    if (delta < 0 || (delta === 0 && entry.primary && !best.primary)) {
      best = entry;
    }
  }
  return best;
}

/**
 * Kinds whose label asserts officialness. On a page the developer does not
 * publish, that label would read as "this vendor's official site is
 * aws.amazon.com", so the evidence tier names the destination instead.
 */
const OFFICIAL_CLAIM_KINDS = new Set<ModelSourceKind>([
  "official-page",
  "official-docs"
]);

/**
 * The value and vocabulary the anchor label should use. Repository, paper, and
 * aggregator labels already describe the destination truthfully at any tier.
 */
export function sourceAnchorLabel(entry: ModelSourceEntry): {
  value: string;
  vocabulary: "sourceKind" | "sourceTier";
} {
  if (entry.tier !== "official" && OFFICIAL_CLAIM_KINDS.has(entry.kind)) {
    return { value: entry.tier, vocabulary: "sourceTier" };
  }
  return { value: entry.kind, vocabulary: "sourceKind" };
}

/** Hostname shown beside the anchor label so the destination is visible. */
export function sourceAnchorHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./u, "");
  } catch {
    return url;
  }
}

/** Ledger order for the links card: strongest evidence first. */
export function sortSourceEntries(
  entries: ModelSourceEntry[]
): ModelSourceEntry[] {
  return [...entries].sort((left, right) => {
    const delta = anchorRank(left) - anchorRank(right);
    if (delta !== 0) return delta;
    return left.url.localeCompare(right.url);
  });
}
